/**
 * Role Management Utilities
 * 
 * The application has 5 distinct platform-level roles:
 * 1. super_admin - System administrator with full access to all groups and system settings
 * 2. group_creator - User who creates groups (can create and manage their groups)
 * 3. group_admin - Group administrator who manages their own group(s)
 * 4. group_clerk - Clerk who can record contributions for groups
 * 5. group_member - Regular member who can view and contribute to their group
 */

export type UserRole = 'super_admin' | 'group_creator' | 'group_admin' | 'group_clerk' | 'group_member';
export type DisplayRole = 'admin' | 'member'; // Simplified role for UI display

/**
 * Check if a user is a super admin
 */
export function isSuperAdmin(role?: string | null): boolean {
  return role === 'super_admin';
}

/**
 * Check if a user is a group creator
 */
export function isGroupCreator(role?: string | null): boolean {
  return role === 'group_creator';
}

/**
 * Check if a user is a group admin (includes super_admin, group_creator, and group_admin)
 */
export function isGroupAdmin(role?: string | null): boolean {
  return role === 'group_admin' || role === 'group_creator' || role === 'super_admin';
}

/**
 * Check if a user is a group clerk
 */
export function isGroupClerk(role?: string | null): boolean {
  return role === 'group_clerk';
}

/**
 * Check if a user is a regular member (group_member)
 */
export function isMember(role?: string | null): boolean {
  return role === 'group_member' || role === 'member'; // Support legacy 'member' for backward compatibility
}

/**
 * Check if a user can manage groups (super_admin, group_creator, or group_admin)
 */
export function canManageGroups(role?: string | null): boolean {
  return role === 'super_admin' || role === 'group_creator' || role === 'group_admin';
}

/**
 * Check if a user can record contributions (group_admin, group_clerk, or super_admin)
 * Note: group_creator cannot record contributions - they can only manage groups
 */
export function canRecordContributions(role?: string | null): boolean {
  return role === 'group_admin' || role === 'group_clerk' || role === 'super_admin';
}

/**
 * Convert database role to display role for UI
 * - super_admin -> 'admin'
 * - group_creator -> 'admin'
 * - group_admin -> 'admin'
 * - group_clerk -> 'admin' (can record contributions)
 * - group_member -> 'member'
 */
export function getDisplayRole(role?: string | null): DisplayRole {
  if (canRecordContributions(role)) {
    return 'admin';
  }
  return 'member';
}

/**
 * Get all roles that should have admin-level access
 */
export function getAdminRoles(): UserRole[] {
  return ['super_admin', 'group_creator', 'group_admin', 'group_clerk'];
}

/**
 * Check if role has access to a specific feature
 */
export function hasAccess(role: string | null | undefined, requiredRole: UserRole | DisplayRole): boolean {
  if (!role) return false;
  
  // If required role is 'admin', check if user is any admin type
  if (requiredRole === 'admin') {
    return canRecordContributions(role);
  }
  
  // If required role is 'member', all roles have access
  if (requiredRole === 'member' || requiredRole === 'group_member') {
    return true;
  }
  
  // Exact role match
  return role === requiredRole;
}

/**
 * Get role display name
 */
export function getRoleDisplayName(role?: string | null): string {
  switch (role) {
    case 'super_admin':
      return 'Super Admin';
    case 'group_creator':
      return 'Group Creator';
    case 'group_admin':
      return 'Group Admin';
    case 'group_clerk':
      return 'Group Clerk';
    case 'group_member':
      return 'Group Member';
    case 'member': // Legacy support
      return 'Member';
    default:
      return 'Unknown';
  }
}

